import { Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { SessionService } from "../session.service";
import { PermissionService } from "../permission.service";
import { SignalsService } from "../signals/signals.service";
import { MediaService, Media } from "../content/media.service";
import {
  ACCOUNT_PREREQUISITES,
  AccountPrerequisiteDefinition,
  AccountPrerequisiteId,
  AccountPrerequisiteInstance,
} from "./account-prerequisites.model";
import { FEATURE_TEMPO_KEY } from "../features.constants";
import { AvailableAccount } from "../accounts/AvailableAccount";

export type AccountPrerequisiteStatus = Partial<
  Record<AccountPrerequisiteId, boolean>
>;

@Injectable({
  providedIn: "root",
})
export class AccountPrerequisitesService {
  constructor(
    private sessionService: SessionService,
    private permissionService: PermissionService,
    private signalsService: SignalsService,
    private mediaService: MediaService,
  ) {}

  /**
   * Returns the first missing prerequisite for the current account, or null if
   * everything is satisfied or not applicable.
   */
  getFirstMissingForCurrentAccount(): Observable<AccountPrerequisiteInstance | null> {
    const account = this.sessionService.currentAccountValue;
    if (!account) {
      return of(null);
    }

    return this.checkAllForAccount(account).pipe(
      map((status) => {
        const missingId = this.findFirstMissingId(status);
        return this.toInstance(missingId, account);
      }),
    );
  }

  private findFirstMissingId(
    status: AccountPrerequisiteStatus,
  ): AccountPrerequisiteId | null {
    for (const def of ACCOUNT_PREREQUISITES) {
      const value = status[def.id];
      if (value === false) {
        return def.id;
      }
    }
    return null;
  }

  private toInstance(
    id: AccountPrerequisiteId | null,
    account: AvailableAccount,
  ): AccountPrerequisiteInstance | null {
    if (!id) {
      return null;
    }

    const def = ACCOUNT_PREREQUISITES.find((p) => p.id === id);
    if (!def) {
      return null;
    }

    return {
      ...def,
      recommendedRoute: def.recommendedRouteFactory(account),
    };
  }

  private checkAllForAccount(
    account: AvailableAccount,
  ): Observable<AccountPrerequisiteStatus> {
    const hasTempo = this.permissionService.hasFeature(
      FEATURE_TEMPO_KEY,
      account,
    );

    const checks: Record<AccountPrerequisiteId, Observable<boolean>> = {
      "account-category": this.checkHasAccountCategory(account),
      "account-app": this.checkHasAccountApp(account),
      "tempo-facility": hasTempo
        ? this.checkHasTempoFacility(account)
        : of(true),
      "tempo-template": hasTempo
        ? this.checkHasTempoTemplate(account)
        : of(true),
      "tempo-mobile-header": hasTempo
        ? this.checkHasMediaWithContext(account, "signal_template_logo")
        : of(true),
      "tempo-mobile-background": hasTempo
        ? this.checkHasMediaWithContext(account, "signal_template_background")
        : of(true),
      "catalog-profile-field": this.checkHasCatalogProfileField(account),
    };

    // Wrap each observable with catchError so a single API failure does not
    // break the whole evaluation (treat failures as "missing").
    const wrappedChecks: Record<AccountPrerequisiteId, Observable<boolean>> =
      {} as any;

    (Object.keys(checks) as AccountPrerequisiteId[]).forEach((id) => {
      wrappedChecks[id] = checks[id].pipe(
        catchError(() => of(false)),
      );
    });

    return forkJoin(wrappedChecks);
  }

  private checkHasAccountCategory(account: AvailableAccount): Observable<boolean> {
    return this.signalsService
      .getSignalCategories(account.accountID)
      .pipe(
        map((response: any) => {
          const categories = Array.isArray(response)
            ? response
            : Array.isArray(response?.records)
            ? response.records
            : [];
          return categories.length > 0;
        }),
      );
  }

  private checkHasAccountApp(account: AvailableAccount): Observable<boolean> {
    const apps = account.apps || [];
    return of(Array.isArray(apps) && apps.length > 0);
  }

  private checkHasTempoFacility(account: AvailableAccount): Observable<boolean> {
    const facilities = account.facilities || [];
    return of(Array.isArray(facilities) && facilities.length > 0);
  }

  private checkHasTempoTemplate(account: AvailableAccount): Observable<boolean> {
    return this.signalsService
      .getSignalTemplates(account.accountID, {
        page: 0,
        perPage: 1,
      })
      .pipe(
        map((response: any) => {
          const records = Array.isArray(response?.records)
            ? response.records
            : Array.isArray(response)
            ? response
            : [];
          return records.length > 0;
        }),
      );
  }

  private checkHasMediaWithContext(
    account: AvailableAccount,
    context: string,
  ): Observable<boolean> {
    return this.mediaService
      .getMedias(account.accountID, {
        page: 0,
        perPage: 1,
        context,
        forCount: false,
      })
      .pipe(
        map((response: any) => {
          const records: Media[] = Array.isArray(response?.records)
            ? response.records
            : Array.isArray(response)
            ? response
            : [];
          return records.some((media) => media.context === context);
        }),
      );
  }

  private checkHasCatalogProfileField(
    account: AvailableAccount,
  ): Observable<boolean> {
    const catalog: any = account.catalog || {};
    const profile = catalog?.app_users?.profile;
    const hasProfileField =
      Array.isArray(profile) && profile.length > 0;
    return of(hasProfileField);
  }
}


